// =============================================
// PÁGINA: EDITAR AUTOMAÇÃO
// =============================================
// Arquivo: AutomationEdit.tsx
// Descrição: Página para editar fluxo de automação existente
// =============================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FlowEditor from '../../components/FlowEditor/FlowEditor';
import FlowHistory from '../../components/Automation/FlowHistory';
import VariableManager from '../../components/Automation/VariableManager';
import DebugPanel from '../../components/Automation/DebugPanel';
import FlowImportExport from '../../components/Automation/FlowImportExport';
import TemplateLibrary from '../../components/Automation/TemplateLibrary';
import { apiService } from '../../services/api';
import './AutomationForm.css';

type SidePanelType = 'none' | 'history' | 'variables' | 'debug' | 'import-export' | 'templates';

const AutomationEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flow, setFlow] = useState<any>(null);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [triggerType, setTriggerType] = useState('keyword');
  const [keywords, setKeywords] = useState('');
  const [showMetadataForm, setShowMetadataForm] = useState(false);
  const [activePanel, setActivePanel] = useState<SidePanelType>('none');
  const [currentNodes, setCurrentNodes] = useState<any[]>([]);
  const [currentEdges, setCurrentEdges] = useState<any[]>([]);

  useEffect(() => {
    loadFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadFlow = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAutomationFlow(parseInt(id!));

      if (response.success && response.data) {
        const flowData = response.data;
        setFlow(flowData);
        setFlowName(flowData.nome);
        setFlowDescription(flowData.descricao || '');
        setTriggerType(flowData.trigger_type || 'keyword');
        setCurrentNodes(flowData.flow_data?.nodes || []);
        setCurrentEdges(flowData.flow_data?.edges || []);

        if (flowData.trigger_config?.keywords) {
          setKeywords(flowData.trigger_config.keywords.join(', '));
        }
      } else {
        alert('Fluxo não encontrado');
        navigate('/automacoes');
      }
    } catch (error) {
      console.error('Erro ao carregar fluxo:', error);
      alert('Erro ao carregar fluxo');
      navigate('/automacoes');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (nodes: any[], edges: any[]) => {
    if (!flowName.trim()) {
      alert('Por favor, insira um nome para o fluxo');
      setShowMetadataForm(true);
      return;
    }

    setSaving(true);
    setCurrentNodes(nodes);
    setCurrentEdges(edges);

    try {
      const updateData = {
        nome: flowName,
        descricao: flowDescription,
        trigger_type: triggerType,
        trigger_config: {
          keywords: keywords.split(',').map(k => k.trim()).filter(k => k)
        },
        flow_data: {
          nodes,
          edges
        }
      };

      const response = await apiService.updateAutomationFlow(
        parseInt(id!),
        updateData
      );

      if (response.success) {
        alert('Fluxo atualizado com sucesso!');
        navigate('/automacoes');
      } else {
        alert('Erro ao atualizar fluxo: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao salvar fluxo:', error);
      alert('Erro ao salvar fluxo');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (window.confirm('Deseja sair sem salvar as alterações?')) {
      navigate('/automacoes');
    }
  };

  const togglePanel = (panel: SidePanelType) => {
    setActivePanel(activePanel === panel ? 'none' : panel);
  };

  // Handlers para FlowHistory
  const handleRestoreVersion = (version: any) => {
    if (window.confirm('Deseja restaurar esta versão? As alterações não salvas serão perdidas.')) {
      setCurrentNodes(version.nodes || []);
      setCurrentEdges(version.edges || []);
      loadFlow(); // Recarregar o fluxo
    }
  };

  // Handlers para FlowImportExport
  const handleImport = (importedData: any) => {
    if (importedData.nodes && importedData.edges) {
      setCurrentNodes(importedData.nodes);
      setCurrentEdges(importedData.edges);
      if (importedData.name) setFlowName(importedData.name);
      if (importedData.description) setFlowDescription(importedData.description);
      alert('Fluxo importado com sucesso! Clique em Salvar para confirmar.');
      setActivePanel('none');
    }
  };

  // Handlers para TemplateLibrary
  const handleApplyTemplate = (template: any) => {
    if (window.confirm('Aplicar este template substituirá o fluxo atual. Continuar?')) {
      setCurrentNodes(template.nodes || []);
      setCurrentEdges(template.edges || []);
      alert('Template aplicado! Clique em Salvar para confirmar.');
      setActivePanel('none');
    }
  };

  if (loading) {
    return (
      <div className="automation-form-page">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Carregando fluxo...</p>
        </div>
      </div>
    );
  }

  if (!flow) {
    return null;
  }

  return (
    <div className="automation-form-page">
      {/* Header */}
      <div className="form-header">
        <button className="back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="header-info">
          <h1>Editar Fluxo</h1>
          <p>{flowName}</p>
        </div>
        <div className="header-actions">
          <button
            className={`tool-btn ${activePanel === 'templates' ? 'active' : ''}`}
            onClick={() => togglePanel('templates')}
            title="Templates"
          >
            <i className="fas fa-puzzle-piece"></i>
          </button>
          <button
            className={`tool-btn ${activePanel === 'import-export' ? 'active' : ''}`}
            onClick={() => togglePanel('import-export')}
            title="Importar/Exportar"
          >
            <i className="fas fa-exchange-alt"></i>
          </button>
          <button
            className={`tool-btn ${activePanel === 'variables' ? 'active' : ''}`}
            onClick={() => togglePanel('variables')}
            title="Variáveis Globais"
          >
            <i className="fas fa-code"></i>
          </button>
          <button
            className={`tool-btn ${activePanel === 'history' ? 'active' : ''}`}
            onClick={() => togglePanel('history')}
            title="Histórico"
          >
            <i className="fas fa-history"></i>
          </button>
          <button
            className={`tool-btn ${activePanel === 'debug' ? 'active' : ''}`}
            onClick={() => togglePanel('debug')}
            title="Debug"
          >
            <i className="fas fa-bug"></i>
          </button>
          <button
            className="metadata-toggle"
            onClick={() => setShowMetadataForm(!showMetadataForm)}
            title="Configurações"
          >
            <i className={`fas fa-${showMetadataForm ? 'times' : 'cog'}`}></i>
          </button>
        </div>
      </div>

      {/* Metadata Form */}
      {showMetadataForm && (
        <div className="metadata-panel">
          <div className="metadata-form">
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="flowName">
                  Nome do Fluxo <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="flowName"
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="flowDescription">Descrição</label>
                <textarea
                  id="flowDescription"
                  value={flowDescription}
                  onChange={(e) => setFlowDescription(e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="triggerType">Tipo de Gatilho</label>
                <select
                  id="triggerType"
                  value={triggerType}
                  onChange={(e) => setTriggerType(e.target.value)}
                >
                  <option value="keyword">Palavra-chave</option>
                  <option value="manual">Manual</option>
                  <option value="schedule">Agendado</option>
                </select>
              </div>

              {triggerType === 'keyword' && (
                <div className="form-field">
                  <label htmlFor="keywords">Palavras-chave</label>
                  <input
                    type="text"
                    id="keywords"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="checklist, iniciar, começar"
                  />
                </div>
              )}
            </div>

            <button
              className="continue-btn"
              onClick={() => setShowMetadataForm(false)}
            >
              Fechar e Editar
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Main Content with Side Panel */}
      <div className={`main-content-area ${activePanel !== 'none' ? 'with-panel' : ''}`}>
        {/* Flow Editor */}
        <div className={`editor-container ${showMetadataForm ? 'hidden' : ''}`}>
          <FlowEditor
            initialNodes={currentNodes}
            initialEdges={currentEdges}
            onSave={handleSave}
          />
        </div>

        {/* Side Panel */}
        {activePanel !== 'none' && (
          <div className="side-panel">
            <div className="side-panel-header">
              <h3>
                {activePanel === 'history' && 'Histórico de Versões'}
                {activePanel === 'variables' && 'Variáveis Globais'}
                {activePanel === 'debug' && 'Debug'}
                {activePanel === 'import-export' && 'Importar/Exportar'}
                {activePanel === 'templates' && 'Templates'}
              </h3>
              <button className="close-panel-btn" onClick={() => setActivePanel('none')}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="side-panel-content">
              {activePanel === 'history' && (
                <FlowHistory
                  currentFlow={{ nodes: currentNodes, edges: currentEdges }}
                  onRestore={handleRestoreVersion}
                />
              )}
              {activePanel === 'variables' && (
                <VariableManager
                  variables={[]}
                  onVariableAdd={() => {}}
                  onVariableUpdate={() => {}}
                  onVariableDelete={() => {}}
                />
              )}
              {activePanel === 'debug' && (
                <DebugPanel
                  nodes={currentNodes}
                  edges={currentEdges}
                  variables={[]}
                />
              )}
              {activePanel === 'import-export' && (
                <FlowImportExport
                  currentFlow={{
                    name: flowName,
                    description: flowDescription,
                    nodes: currentNodes,
                    edges: currentEdges
                  }}
                  onImport={handleImport}
                />
              )}
              {activePanel === 'templates' && (
                <TemplateLibrary
                  templates={[]}
                  onTemplateSelect={handleApplyTemplate}
                  currentFlowData={{ nodes: currentNodes, edges: currentEdges }}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Saving overlay */}
      {saving && (
        <div className="saving-overlay">
          <div className="saving-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Salvando alterações...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationEdit;
